import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Alert,
  CircularProgress,
  Typography
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import projectService, { 
  ProjectContext, 
  CreateProjectContextData, 
  UpdateProjectContextData,
  CONTEXT_TYPES 
} from '../../services/projectService';

interface ProjectContextFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  context?: ProjectContext | null;
  defaultProjectCode?: string;
  currentUser: string;
}

interface FormData {
  project_code: string;
  context_type: string;
  title: string;
  content: string;
  is_active: boolean;
}

const ProjectContextForm: React.FC<ProjectContextFormProps> = ({
  open,
  onClose,
  onSuccess,
  context,
  defaultProjectCode,
  currentUser
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contextTypes, setContextTypes] = useState<string[]>([]);

  const isEditing = Boolean(context);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty }
  } = useForm<FormData>({
    defaultValues: {
      project_code: context?.project_code || defaultProjectCode || '',
      context_type: context?.context_type || '',
      title: context?.title || '',
      content: context?.content || '',
      is_active: context?.is_active ?? true
    }
  });

  useEffect(() => {
    if (open) {
      loadContextTypes();
      if (context) {
        reset({
          project_code: context.project_code,
          context_type: context.context_type,
          title: context.title || '',
          content: context.content,
          is_active: context.is_active
        });
      } else {
        reset({
          project_code: defaultProjectCode || '',
          context_type: '',
          title: '',
          content: '',
          is_active: true
        });
      }
    }
  }, [open, context, defaultProjectCode, reset]);

  const loadContextTypes = async () => {
    try {
      const types = await projectService.getContextTypes();
      setContextTypes(types);
    } catch (err) {
      console.error('Error loading context types:', err);
      // Fallback to predefined types
      setContextTypes(Object.values(CONTEXT_TYPES));
    }
  };

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError(null);

    try {
      if (isEditing && context) {
        const updateData: UpdateProjectContextData = {
          context_type: data.context_type,
          title: data.title || undefined,
          content: data.content,
          is_active: data.is_active,
          updated_by: currentUser
        };
        await projectService.updateProjectContext(context.id, updateData);
      } else {
        const createData: CreateProjectContextData = {
          project_code: data.project_code.toUpperCase(),
          context_type: data.context_type,
          title: data.title || undefined,
          content: data.content,
          is_active: data.is_active,
          created_by: currentUser
        };
        await projectService.createProjectContext(createData);
      }

      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al guardar el contexto');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      reset();
      setError(null);
      onClose();
    }
  };

  const getContextTypeLabel = (contextType: string) => {
    const labels: { [key: string]: string } = {
      description: 'Descripción',
      amenities: 'Amenidades',
      location_details: 'Detalles de Ubicación',
      pricing: 'Precios',
      specifications: 'Especificaciones',
      gallery: 'Galería',
      contact_info: 'Información de Contacto',
      custom: 'Personalizado'
    };
    return labels[contextType] || contextType;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle>
        {isEditing ? 'Editar Contexto de Proyecto' : 'Crear Contexto de Proyecto'}
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box display="flex" flexDirection="column" gap={3}>
            <Controller
              name="project_code"
              control={control}
              rules={{ 
                required: 'El código del proyecto es requerido',
                minLength: { value: 1, message: 'El código debe tener al menos 1 carácter' }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Código del Proyecto"
                  fullWidth
                  error={!!errors.project_code}
                  helperText={errors.project_code?.message}
                  disabled={isEditing}
                  placeholder="Ej: PROJ001"
                  onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                />
              )}
            />

            <Controller
              name="context_type"
              control={control}
              rules={{ required: 'El tipo de contexto es requerido' }}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.context_type}>
                  <InputLabel>Tipo de Contexto</InputLabel>
                  <Select {...field} label="Tipo de Contexto">
                    {contextTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {getContextTypeLabel(type)}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.context_type && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                      {errors.context_type.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Título (Opcional)"
                  fullWidth
                  placeholder="Título descriptivo para este contexto"
                />
              )}
            />

            <Controller
              name="content"
              control={control}
              rules={{ 
                required: 'El contenido es requerido',
                minLength: { value: 10, message: 'El contenido debe tener al menos 10 caracteres' }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Contenido"
                  fullWidth
                  multiline
                  rows={8}
                  error={!!errors.content}
                  helperText={errors.content?.message || 'Información detallada sobre este aspecto del proyecto'}
                  placeholder="Ingresa la información contextual del proyecto..."
                />
              )}
            />

            <Controller
              name="is_active"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={field.onChange}
                      color="primary"
                    />
                  }
                  label="Contexto activo"
                />
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !isDirty}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ProjectContextForm;